import { useState, useEffect, useRef } from 'react';
import Services from '../services/Services';
import { EventListItem } from '../types';
import { registerAbortController, unregisterAbortController, isLoggingOut } from '../utils/requestCancellation';
import { useGeneral } from '../contexts';

interface UseEventsOptions {
  token: string | null;
  userID?: string;
  channelID?: string;
  loadingKey?: string; // Optional key for centralized loading state
  useClosestDates?: boolean; // Use closest dates endpoint instead of popular events
  useCertificates?: boolean; // Use events-with-certificates endpoint instead of popular events
}

export const useEvents = (options: UseEventsOptions) => {
  const { token, userID, channelID, loadingKey, useClosestDates, useCertificates } = options;
  const { setLoading, isLoading } = useGeneral();
  const [events, setEvents] = useState<EventListItem[]>([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  
  // Use centralized loading if key is provided, otherwise use local state
  // Note: centralized loading starts as false; keep localLoading true initially
  // to avoid flashing empty states before the first request starts.
  const loading = loadingKey ? (isLoading(loadingKey) || localLoading) : localLoading;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    registerAbortController(abortController);

    const fetchEvents = async () => {
      if (!token || isLoggingOut()) {
        if (isMountedRef.current) {
          if (loadingKey) {
            setLoading(loadingKey, false);
          }
          setLocalLoading(false);
        }
        return;
      }

      try {
        if (isMountedRef.current) {
          if (loadingKey) {
            setLoading(loadingKey, true);
          }
          setLocalLoading(true);
          setError(null);
        }

        let response;
        if (userID) {
          response = await Services.SocialHiveGetUsersEvents(token, userID, abortController);
        } else if (channelID) {
          response = await Services.SocialHiveGetEvents(token, channelID, abortController);
        } else {
          if (useCertificates) {
            response = await Services.SocialHiveGetEventsWithCertificates(token, abortController);
          } else if (useClosestDates) {
            response = await Services.SocialHiveGetClosestDatesChannelEvents(token, abortController);
          } else {
            response = await Services.SocialHiveGetPopularChannelEvents(token, abortController);
          }
        }

        // Check if request was aborted or component unmounted
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }

        if (response.data.status && response.data.result) {
          setEvents(response.data.result);
        } else {
          setError(response.data.message || 'Failed to fetch events');
        }
      } catch (err: any) {
        // Ignore abort errors
        if (err.name === 'AbortError' || err.name === 'CanceledError' || err.message?.includes('Logout in progress')) {
          return;
        }

        if (isMountedRef.current) {
          setError(err.message || 'An error occurred');
          setEvents([]);
        }
      } finally {
        if (isMountedRef.current && !abortController.signal.aborted) {
          if (loadingKey) {
            setLoading(loadingKey, false);
          }
          setLocalLoading(false);
        } else if (loadingKey) {
          // Clear loading even if unmounted to prevent stuck state
          setLoading(loadingKey, false);
        }
      }
    };

    fetchEvents();

    return () => {
      abortController.abort();
      unregisterAbortController(abortController);
      // Clear loading state on cleanup to prevent stuck state
      if (loadingKey) {
        setLoading(loadingKey, false);
      }
    };
  }, [token, userID, channelID, loadingKey, useClosestDates, useCertificates, setLoading]);

  return { events, loading, error, refetch: () => {} };
};
